import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import CustomAppBar from "../../components/AppBar";
import {
  Container,
  TextField,
  Grid,
  Divider,
  Box,
  Pagination,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import {
  CustomChartsTitle,
  CustomTitle,
} from "../../components/Custom/CustomTypography";
import { useTranslation } from "react-i18next";
import CustomDetailedList from "../../components/Custom/CustomDetailedList";
import LoaderContainer from "../../components/LoaderContainer";
import CommodityIcon from "../../assets/Icon/CommodityBuyerIcon.png";
import { MARKET_PRICES_ENDPOINT } from "../../globals/endpoints";
import useSharedLocation from "../../hooks/useSharedLocation";

const MarketPrices = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [stateDistrictsData, setStateDistrictsData] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [commodities, setCommodities] = useState([]);
  const [selectedCommodity, setSelectedCommodity] = useState("");
  const [marketPrices, setMarketPrices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const { location } = useSharedLocation();

  // Fetch commodity prices based on user's location
  const fetchInitialData = async (district, page = 1) => {
    // Get today's date in the format like 11 Jul 2024
    const today = new Date();
    const options = { day: "2-digit", month: "short", year: "numeric" };
    const date = today.toLocaleDateString("en-GB", options);

    // Manually construct the URL with %20 encoding for spaces
    const encodedDistrict = encodeURIComponent(district);
    const encodedDate = date.replace(/ /g, "%20"); // Replace spaces with %20 manually

    const url = `${MARKET_PRICES_ENDPOINT}initial-data?date=${encodedDate}&district=${encodedDistrict}&limit=10&page=${page}`;

    try {
      const response = await axios.get(url, {
        headers: {
          accept: "application/xml",
        },
      });
      // Handle the response as needed
      setMarketPrices(response.data.data);
      setTotalPages(response.data.pagination.totalPages); // Update total pages from response
      setIsLoading(false);
    } catch (err) {
      console.error("Failed to fetch initial data", err);
      setIsLoading(false);
    }
  };

  //for pagination when page changes fetch the next set of data
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    fetchInitialData(location.district, value); // Fetch data for the new page
  };

  //Using to fetch the states list
  const fetchStates = async () => {
    try {
      const response = await axios.get(`${MARKET_PRICES_ENDPOINT}`);
      const data = response.data;
      setStateDistrictsData(data);
      const states = Object.keys(data);
      setStates(states);
    } catch (err) {
      console.error(err);
    }
  };

  // Fetch the list of commodities when state and district are selected
  const fetchCommodities = async (state, district) => {
    if (state && district) {
      try {
        const response = await axios.get(`${MARKET_PRICES_ENDPOINT}`, {
          params: { state, district },
        });
        const commodities = Object.keys(response.data);
        setCommodities(commodities);
      } catch (err) {
        console.error(err);
      }
    }
  };

  // Fetch Commodity Prices for the selected state, district and commodity
  const fetchCommodityPrices = async (state, district, commodity) => {
    if (state && district && commodity) {
      // Fetch current date
      const today = new Date();
      const options = { day: "2-digit", month: "short", year: "numeric" };
      const date = today.toLocaleDateString("en-GB", options);

      // Trim the state, district, and commodity names to remove trailing spaces
      const trimmedState = state.trim();
      const trimmedDistrict = district.trim();
      const trimmedCommodity = commodity.trim();

      // Construct the URL with %20 encoding for spaces
      const encodedState = encodeURIComponent(trimmedState);
      const encodedDistrict = encodeURIComponent(trimmedDistrict);
      const encodedCommodity = encodeURIComponent(trimmedCommodity);
      const encodedDate = date.replace(/ /g, "%20"); // Replace spaces with %20 manually

      const url = `${MARKET_PRICES_ENDPOINT}?state=${encodedState}&district=${encodedDistrict}&commodity=${encodedCommodity}&date=${encodedDate}`;

      try {
        const response = await axios.get(url);
        const marketPrices = response.data;
        setMarketPrices(marketPrices);
      } catch (err) {
        console.error(err);
      }
    }
  };

  //to fetch the marketPrice of the commodity when all the fields are selected
  useEffect(() => {
    if (selectedState && selectedDistrict && selectedCommodity) {
      fetchCommodityPrices(selectedState, selectedDistrict, selectedCommodity);
    }
  }, [selectedState, selectedDistrict, selectedCommodity]);

  //to fetch list of commodities when state and district are selected
  useEffect(() => {
    if (selectedState && selectedDistrict) {
      fetchCommodities(selectedState, selectedDistrict);
    }
  }, [selectedState, selectedDistrict]);

  //to fetch the initial data based on the users location
  useEffect(() => {
    if (location && location.district) {
      fetchInitialData(location.district, currentPage); // Pass currentPage to fetchInitialData
    }
  }, [location, currentPage]);

  useEffect(() => {
    fetchStates();
  }, []);

  const handleStateChange = (event, newValue) => {
    if (newValue) {
      setSelectedState(newValue);
      const districtsList = stateDistrictsData[newValue].districts; //fetching the list of districts based on selected state
      const districts = Object.keys(districtsList);
      setDistricts(districts);
      setSelectedDistrict("");
      setSelectedCommodity("");
    } else {
      setSelectedState("");
      setDistricts([]);
      setSelectedDistrict("");
    }
  };

  const handleDistrictChange = (event, newValue) => {
    setSelectedDistrict(newValue || "");
  };

  return (
    <>
      <Helmet>
        <title>Market Rate - Farmapp</title>
      </Helmet>
      <LoaderContainer loading={isLoading}>
        <CustomAppBar />
        <Container maxWidth="xs">
          <CustomTitle>{t("Market Rate")}</CustomTitle>

          <Grid container spacing={2} mt={1}>
            <Grid item xs={6}>
              <Autocomplete
                disablePortal
                id="combo-box-demo"
                options={states}
                value={selectedState}
                renderInput={(params) => (
                  <TextField {...params} label={t("Select State")} />
                )}
                onChange={handleStateChange}
              />
            </Grid>

            <Grid item xs={6}>
              <Autocomplete
                disablePortal
                id="combo-box-demo"
                options={districts}
                value={selectedDistrict}
                renderInput={(params) => (
                  <TextField {...params} label={t("Select District")} />
                )}
                onChange={handleDistrictChange}
              />
            </Grid>

            <Grid item xs={6}>
              <Autocomplete
                disablePortal
                id="combo-box-demo"
                options={commodities}
                value={selectedCommodity}
                renderInput={(params) => (
                  <TextField {...params} label={t("Select Commodity")} />
                )}
                onChange={(event, newValue) => {
                  setSelectedCommodity(newValue || "");
                }}
              />
            </Grid>
          </Grid>

          <Divider
            sx={{
              margin: "auto",
              width: "150px",
              marginTop: "25px",
              backgroundColor: "#A5A5A5",
            }}
          />

          <Box mt={3}>
            {marketPrices.length === 0 && (
              <>
                <div
                  style={{
                    marginTop: "50px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <img
                    src={CommodityIcon}
                    alt="No Commodity Available"
                    style={{
                      width: "127px",
                      height: "127px",
                      marginBottom: "10px",
                    }}
                  />

                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, textAlign: "center" }}
                  >
                    {t("Currently No Commodity Available")}
                  </Typography>
                </div>
              </>
            )}

            {marketPrices.length != 0 && (
              <CustomChartsTitle sx={{ marginTop: "20px" }}>
                {t("Commodity Price")}
              </CustomChartsTitle>
            )}

            <Box mt={2}>
              {marketPrices.map((marketPrice) => (
                <CustomDetailedList
                  key={marketPrice.id}
                  id={marketPrice.id}
                  data={[
                    {
                      label: t("Commodity Name"),
                      value: marketPrice.commodity,
                    },
                    {
                      label: t("Average market rate (Rs./Quintal)"),
                      value: `Rs. ${marketPrice.modalPrice}`,
                    },
                    { label: t("Market"), value: marketPrice.market },

                    { label: t("Variety"), value: marketPrice.variety },
                    {
                      label: t("Grade"),
                      value: marketPrice.grade,
                    },
                    { label: t("Last Updated Date"), value: marketPrice.date },
                  ]}
                  buttonList={[]}
                />
              ))}
              {marketPrices.length != 0 && (
                <Box mt={3} display="flex" justifyContent="center">
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Container>
      </LoaderContainer>
    </>
  );
};

export default MarketPrices;
